// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
import type { ViewContext } from '../viewContext';
import { SEX_COLOUR } from '../colours';
import { glossaryTerm } from '../glossary';
import { escapeHtml, formatNumber, formatPercent } from '../utils/format';
import { rankListHtml, topNAt, viewHead, wireNameRows } from './shared';

export function renderOverview(root: HTMLElement, ctx: ViewContext): void {
  const { meta, names } = ctx.data;
  const y = meta.latestYear;
  const nswBoy = meta.latest['NSW-boy'];
  const nswGirl = meta.latest['NSW-girl'];
  const qldBoy = meta.latest['QLD-boy'];
  const qldGirl = meta.latest['QLD-girl'];

  // Diversity contrast: NSW boys, #1 count then vs now.
  const boyDiv = ctx.data.agg.diversity['NSW-boy'] || [];
  const first = boyDiv[0];
  const last = boyDiv[boyDiv.length - 1];

  const no1Card = (sk: string, dp: typeof nswBoy, cls: string) =>
    dp
      ? `<div class="no1-card" data-key="${escapeHtml(dp.no1.toUpperCase())}" role="button" tabindex="0">
          <div class="no1-crown">#1 ${sk} · ${y}</div>
          <div class="no1-name ${cls}">${escapeHtml(dp.no1)}</div>
          <div class="stat-note">${formatNumber(dp.no1Count)} babies</div>
        </div>`
      : '';

  root.innerHTML = `
    ${viewHead(
      'Australia\'s baby names, 1952–' + y,
      `Every name in the top-100 boys\' and girls\' lists that New South Wales and Queensland have published each year. ${glossaryTerm(
        'coverage',
        'Two states, close to half of all births',
      )} — and the ${glossaryTerm('concentration', 'fashions are national')}. Type a name in the search box to trace its own story.`,
    )}

    <div class="hero">
      <div class="panel" style="display:flex;flex-direction:column;justify-content:center">
        <div class="no1-crown">The #1 name isn't what it used to be</div>
        <div class="big-name" style="margin:var(--space-sm) 0">${first ? escapeHtml(first.no1) : ''} → ${last ? escapeHtml(last.no1) : ''}</div>
        <p class="hero-lead">
          In <strong>${first ? first.year : ''}</strong>, NSW's top boys' name (${first ? escapeHtml(first.no1) : ''}) went to
          <strong>${first ? formatNumber(first.no1Count) : ''}</strong> babies — ${first ? formatPercent(first.no1Share) : ''} of the top 100.
          By <strong>${y}</strong>, the #1 name (${last ? escapeHtml(last.no1) : ''}) reached just
          <strong>${last ? formatNumber(last.no1Count) : ''}</strong> — ${last ? formatPercent(last.no1Share) : ''}. Naming has fragmented.
        </p>
        <div style="margin-top:var(--space-md)">
          <button class="chip" data-goto="diversity" style="font-size:var(--font-size-sm)">See the whole shift →</button>
        </div>
      </div>
      <div>
        <div class="no1-crown" style="margin-bottom:var(--space-sm)">Most popular names in ${y}</div>
        <div class="no1-cards">
          ${no1Card('NSW boys', nswBoy, 'boy')}
          ${no1Card('NSW girls', nswGirl, 'girl')}
          ${no1Card('QLD boys', qldBoy, 'boy')}
          ${no1Card('QLD girls', qldGirl, 'girl')}
        </div>
      </div>
    </div>

    <div class="stat-grid">
      <div class="stat"><div class="stat-label">Distinct names</div><div class="stat-value">${formatNumber(meta.totals.distinctNames)}</div><div class="stat-note">across ${formatNumber(meta.totals.records)} yearly rankings</div></div>
      <div class="stat"><div class="stat-label">Years covered</div><div class="stat-value">${y - 1952 + 1}</div><div class="stat-note">NSW 1952 · QLD 1960 onward</div></div>
      <div class="stat"><div class="stat-label">Top-10 share, ${first ? first.year : ''}</div><div class="stat-value accent">${first ? formatPercent(first.top10Share, 0) : '—'}</div><div class="stat-note">of top-100 NSW boys</div></div>
      <div class="stat"><div class="stat-label">Top-10 share, ${y}</div><div class="stat-value boy">${last ? formatPercent(last.top10Share, 0) : '—'}</div><div class="stat-note">the same measure today</div></div>
    </div>

    <div class="grid-2">
      <div class="panel">
        <div class="panel-title">Top 10 boys' names, ${y}</div>
        <div class="panel-sub">New South Wales. Click a name to see its whole history.</div>
        <div data-role="boys"></div>
      </div>
      <div class="panel">
        <div class="panel-title">Top 10 girls' names, ${y}</div>
        <div class="panel-sub">New South Wales. Click a name to see its whole history.</div>
        <div data-role="girls"></div>
      </div>
    </div>

    <div class="callout">
      <strong>New here?</strong> Try the <button class="chip" data-goto="finder">Name Finder</button> to chart any name,
      <button class="chip" data-goto="charts">The Charts</button> to watch the top names cross over the decades, or
      <button class="chip" data-goto="movers">Risers &amp; Fallers</button> to see what's climbing now.
    </div>
  `;

  (root.querySelector('[data-role="boys"]') as HTMLElement).innerHTML = rankListHtml(topNAt(names, 'NSW-boy', y, 10), SEX_COLOUR.boy);
  (root.querySelector('[data-role="girls"]') as HTMLElement).innerHTML = rankListHtml(topNAt(names, 'NSW-girl', y, 10), SEX_COLOUR.girl);

  wireNameRows(root, ctx.openName);
  root.querySelectorAll('[data-goto]').forEach((b) =>
    b.addEventListener('click', () => ctx.goTo(b.getAttribute('data-goto') as never)),
  );
}
